import React from "react"
import { Query, ApolloConsumer, Mutation } from "react-apollo"
import { Redirect } from "react-router"
import { Waypoint } from "react-waypoint"
import gql from "graphql-tag"
import moment from "moment"
import { PRODUCT_INGESTION_STATUS } from "../constants"

class ProductIngestions extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      loadingMoreRows: false,
      loadingMoreErrors: false,
      alertMessage: "",
      success: false,
      viewErrorsFor: null,
    }
  }

  dismissAlert = () => {
    this.setState({ alertMessage: "" })
  }

  viewErrors = (id) => {
    this.setState({
      viewErrorsFor: id,
    })
  }

  closeErrors = () => {
    this.setState({
      viewErrorsFor: null,
    })
  }

  _renderButton = () => {
    return (
      <Mutation mutation={ProductIngestionMutation}>
        {(mutate, info) => {
          const doMutate = () => {
            if (!info.loading) {
              mutate({
                update: (store, { data: { productIngestion } }) => {
                  if (
                    productIngestion &&
                    productIngestion.payload === "success" &&
                    productIngestion.ingestion
                  ) {
                    try {
                      const recentIngestionData = store.readQuery({
                        query: MostRecentIngestionQuery,
                      })
                      store.writeQuery({
                        query: MostRecentIngestionQuery,
                        data: {
                          ...recentIngestionData,
                          canStartProductIngestion: false,
                          productIngestion: productIngestion.ingestion,
                        },
                      })
                    } catch (err) {
                      console.log(
                        "Error reading recent ingestion from store: ",
                        err
                      )
                    }

                    try {
                      const ingestionsData = store.readQuery({
                        query: ProductIngestionsQuery,
                      })
                      store.writeQuery({
                        query: ProductIngestionsQuery,
                        data: {
                          ...ingestionsData,
                          productIngestions: [
                            productIngestion.ingestion,
                            ...(ingestionsData.productIngestions || []),
                          ],
                        },
                      })
                    } catch (err) {
                      console.log("Error reading ingestions from store: ", err)
                    }
                  }
                },
              })
                .then(({ data }) => {
                  this.setState(
                    {
                      alertMessage: data.productIngestion.message,
                      success: data.productIngestion.payload === "success",
                    },
                    () => {
                      if (data.productIngestion.payload === "success") {
                        setTimeout(() => this.dismissAlert(), 5000)
                      }
                    }
                  )
                })
                .catch((err) => {
                  console.log("Mutation error: ", err)
                  this.setState({
                    alertMessage: "An unexpected error occurred!",
                    success: false,
                  })
                })
            }
          }

          return (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!this.props.canStartNewIngestion}
              onClick={doMutate}
            >
              Begin Ingestion
            </button>
          )
        }}
      </Mutation>
    )
  }

  _renderIngestionHistory = () => {
    return (
      <div className="mt-5">
        <h3>Ingestion History</h3>
        <Query query={ProductIngestionsQuery} fetchPolicy="cache-and-network">
          {(queryProps) => {
            if (queryProps.loading && !queryProps.data) {
              return (
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              )
            }
            if (queryProps.error) {
              return (
                <div className="alert alert-warning" role="alert">
                  An error occurred while fetching past product ingestions.
                  <a
                    href="#"
                    className="alert-link float-right"
                    onClick={(e) => {
                      e.preventDefault()
                      queryProps.refetch()
                    }}
                  >
                    Retry
                  </a>
                </div>
              )
            }

            if (
              !queryProps.data ||
              !queryProps.data.productIngestions ||
              queryProps.data.productIngestions.length === 0
            ) {
              return <p>Nothing to show!</p>
            }

            const loadMore = () => {
              if (!this.state.loadingMoreRows && !queryProps.loading) {
                this.setState({ loadingMoreRows: true }, () => {
                  queryProps
                    .fetchMore({
                      variables: {
                        startingAfter:
                          queryProps.data.productIngestions[
                            queryProps.data.productIngestions.length - 1
                          ].id,
                      },
                      updateQuery: (previousResult, { fetchMoreResult }) => {
                        if (!fetchMoreResult) return previousResult

                        return Object.assign({}, previousResult, {
                          productIngestions: [
                            ...(previousResult.productIngestions || []),
                            ...(fetchMoreResult.productIngestions || []),
                          ],
                        })
                      },
                    })
                    .then(() => this.setState({ loadingMoreRows: false }))
                    .catch(() => this.setState({ loadingMoreRows: false }))
                })
              }
            }

            return (
              <ul className="list-group list-group-flush">
                {queryProps.data.productIngestions.map((row) => {
                  let durationText = "N/A"
                  if (row.status !== PRODUCT_INGESTION_STATUS.RUNNING) {
                    let duration = moment.duration(row.duration, "millisecond")
                    let hours = Math.floor(duration.asHours())
                    let minutes = duration.minutes()
                    let seconds = duration.seconds()

                    if (hours === 0) {
                      durationText = `${minutes}:${("0" + seconds).slice(-2)}`
                    } else {
                      durationText = `${hours}:${("0" + minutes).slice(-2)}:${(
                        "0" + seconds
                      ).slice(-2)}`
                    }
                  }

                  let statusText, statusColor
                  switch (row.status) {
                    case PRODUCT_INGESTION_STATUS.RUNNING:
                      statusText = "Running"
                      statusColor = "secondary"
                      break
                    case PRODUCT_INGESTION_STATUS.COMPLETE:
                      statusText = "Completed"
                      statusColor = "success"
                      break
                    case PRODUCT_INGESTION_STATUS.ERROR:
                      statusText = "Error"
                      statusColor = "danger"
                      break
                  }

                  return (
                    <li key={row.id} className="list-group-item">
                      <div className="d-flex w-100 justify-content-between align-items-start">
                        <h5 className="mb-2">ID: {row.id}</h5>
                        <span className={`badge badge-${statusColor}`}>
                          {statusText}
                        </span>
                      </div>
                      <small className="text-muted">
                        <b>Started at: </b>{" "}
                        {moment(row.startedAt).format("l LTS")}
                      </small>
                      <br />
                      <small className="text-muted">
                        <b>Duration: </b> {durationText}
                      </small>
                      {row.errorCount > 0 && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm float-right"
                          onClick={() => this.viewErrors(row.id)}
                        >
                          View {row.errorCount} Error
                          {row.errorCount !== 1 && "s"}
                        </button>
                      )}
                    </li>
                  )
                })}
                <Waypoint onEnter={loadMore} fireOnRapidScroll={true} />
              </ul>
            )
          }}
        </Query>
      </div>
    )
  }

  _renderViewErrorsModal = () => {
    if (this.state.viewErrorsFor) {
      return (
        <div
          className="modal d-block"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title" id="error-modal-title">
                    Ingestion Errors
                  </h5>
                  <p className="sublabel">
                    <b>Ingestion ID:</b> {this.state.viewErrorsFor}
                  </p>
                </div>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={this.closeErrors}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <Query
                  query={ProductIngestionErrorsQuery}
                  variables={{ id: this.state.viewErrorsFor }}
                >
                  {(queryProps) => {
                    if (queryProps.loading && !queryProps.data) {
                      return "Loading..."
                    } else if (queryProps.error) {
                      return "Unable to fetch data for this ingestion!"
                    } else if (!queryProps.data.productIngestion) {
                      return "Entry not found!"
                    } else if (
                      queryProps.data.productIngestion.errorCount === 0
                    ) {
                      return "Nothing to show!"
                    }

                    const loadMore = () => {
                      if (
                        !this.state.loadingMoreErrors &&
                        !queryProps.loading
                      ) {
                        this.setState({ loadingMoreErrors: true }, () => {
                          queryProps
                            .fetchMore({
                              variables: {
                                offset:
                                  queryProps.data.productIngestion.errorLogs
                                    .length,
                              },
                              updateQuery: (
                                previousResult,
                                { fetchMoreResult }
                              ) => {
                                if (!fetchMoreResult) return previousResult

                                return Object.assign({}, previousResult, {
                                  productIngestion: {
                                    ...fetchMoreResult.productIngestion,
                                    errorLogs: [
                                      ...(previousResult.productIngestion
                                        .errorLogs || []),
                                      ...(fetchMoreResult.productIngestion
                                        .errorLogs || []),
                                    ],
                                  },
                                })
                              },
                            })
                            .then(() =>
                              this.setState({ loadingMoreErrors: false })
                            )
                            .catch(() =>
                              this.setState({ loadingMoreErrors: false })
                            )
                        })
                      }
                    }

                    return (
                      <ul className="list-group list-group-flush">
                        {queryProps.data.productIngestion.errorLogs.map(
                          (row, i) => (
                            <li key={row.id} className="list-group-item d-flex">
                              <h6 className="font-weight-normal">{i + 1}.</h6>
                              <div className="flex-fill ml-2">
                                <h6 className="mb-1">{row.location}</h6>
                                <p className="mb-1 font-weight-light">
                                  {row.description}
                                </p>
                                <small className="text-muted">
                                  {moment(row.timestamp).format("l LTS")}
                                </small>
                              </div>
                            </li>
                          )
                        )}
                        <Waypoint onEnter={loadMore} fireOnRapidScroll={true} />
                      </ul>
                    )
                  }}
                </Query>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={this.closeErrors}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  render() {
    return (
      <div className="container">
        <h2>Product Ingestion</h2>
        {this._renderButton()}
        {this.state.alertMessage && (
          <div
            className={`alert alert-${
              this.state.success ? "success" : "danger"
            } mt-2`}
          >
            {this.state.alertMessage}
            <button type="button" className="close" onClick={this.dismissAlert}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}
        <hr />
        {this._renderIngestionHistory()}
        {this._renderViewErrorsModal()}
      </div>
    )
  }
}

const ProductIngestionsWithData = (props) => {
  return (
    <ApolloConsumer>
      {(client) => (
        <Query
          query={MostRecentIngestionQuery}
          fetchPolicy="network-only"
          pollInterval={10 * 1000}
          onCompleted={(queryData) => {
            if (queryData && queryData.productIngestion) {
              try {
                let data = client.readQuery({ query: ProductIngestionsQuery })

                if (
                  !data.productIngestions ||
                  !data.productIngestions[0] ||
                  data.productIngestions[0].id !== queryData.productIngestion.id
                ) {
                  client.writeQuery({
                    query: ProductIngestionsQuery,
                    data: {
                      ...data,
                      productIngestions: [
                        queryData.productIngestion,
                        ...(data.productIngestions || []),
                      ],
                    },
                  })
                }
              } catch (err) {
                console.log("Nothing in the cache for productIngestions query!")
              }
            }
          }}
        >
          {(queryProps) => {
            if (
              queryProps.loading &&
              !queryProps.error &&
              queryProps.data &&
              queryProps.data.viewer &&
              (!queryProps.data.viewer.isUserAuthenticated ||
                !queryProps.data.viewer.roles.some(
                  (role) =>
                    role.toLowerCase() === "etr master admin" ||
                    role.toLowerCase() === "etr research admin"
                ))
            ) {
              return <Redirect to="/" />
            }

            return (
              <ProductIngestions
                {...props}
                canStartNewIngestion={
                  queryProps.data && queryProps.data.canStartProductIngestion
                }
              />
            )
          }}
        </Query>
      )}
    </ApolloConsumer>
  )
}

const MostRecentIngestionQuery = gql`
  query MostRecentIngestionQuery {
    viewer {
      id
      userId
      isUserAuthenticated
      roles
    }
    canStartProductIngestion
    productIngestion {
      id
      startedAt
      endedAt
      duration
      status
      errorCount
    }
  }
`

const ProductIngestionsQuery = gql`
  query ProductIngestionsQuery($limit: Int, $startingAfter: String) {
    productIngestions(limit: $limit, startingAfter: $startingAfter) {
      id
      startedAt
      endedAt
      duration
      status
      errorCount
    }
  }
`

const ProductIngestionErrorsQuery = gql`
  query ProductIngestionErrorsQuery($id: String!, $limit: Int, $offset: Int) {
    productIngestion(id: $id) {
      id
      startedAt
      endedAt
      duration
      errorCount
      errorLogs(limit: $limit, offset: $offset) {
        id
        timestamp
        location
        description
      }
    }
  }
`

const ProductIngestionMutation = gql`
  mutation ProductIngestionMutation {
    productIngestion {
      payload
      message
      ingestion {
        id
        startedAt
        endedAt
        duration
        status
        errorCount
      }
    }
  }
`

export default ProductIngestionsWithData
